import {
  type NestedRadioGroupProps,
  toExtendedOption,
} from "@hightouch/formkit";
import type { SharedProps } from ".";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  NestedRadioGroup,
  useMissingFormkitRelationships,
} from "src/formkit/components";
import { useFormkitContext } from "src/formkit/components/formkit-context";
import { useMemo } from "react";

export const FKNestedRadioGroup = ({
  name,
  rootKey,
  listKey,
  options,
  query,
  isDisabled,
}: NestedRadioGroupProps & { listKey: string } & SharedProps) => {
  const { getValues, setValue } = useFormContext();
  const { getUnavailableOptionReason } = useFormkitContext();
  // Watched, not read once, so the picker updates when only the path changes.
  // The same account can sit under two roots, and picking the other copy keeps the field value.
  const selectedPath = useWatch({ name: listKey });

  useMissingFormkitRelationships(
    name,
    useMemo(
      () => (rootKey ? [listKey, rootKey] : [listKey]),
      [listKey, rootKey],
    ),
  );

  return (
    <Controller
      name={name}
      render={({ field }) => (
        <NestedRadioGroup
          loading={query.isFetching}
          isDisabled={isDisabled}
          options={toExtendedOption(query.enabled ? query.data : options)}
          reload={query.refetch}
          value={selectedPath}
          getOptionDisabledReason={
            getUnavailableOptionReason
              ? (optionValue) =>
                  getUnavailableOptionReason(name, optionValue, getValues())
              : undefined
          }
          onChange={(value: string[]) => {
            field.onChange(value[value.length - 1]);
            setValue(listKey, value);
            if (rootKey) {
              setValue(rootKey, value[0]);
            }
          }}
        />
      )}
    />
  );
};
